import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatApiError } from "@/api";
import { Upload, X, Plus, Trash2, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { mediaUrl } from "@/web/useWebApi";
import { useBuilderData } from "@/web/BuilderContext";

function ModuleEntityField({ value, onChange, testid }) {
  const { modules } = useBuilderData();
  const forms = modules?.forms || [];
  return (
    <div className="space-y-1">
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-full px-2 text-sm border border-[#DEE2E6] bg-white" data-testid={testid}>
        <option value="">— choisir une fiche de module —</option>
        {forms.map((f) => <option key={f.ref} value={f.ref}>{f.module_name} › {f.label} ({f.fields.length} champs{f.file_fields?.length ? " + fichiers" : ""})</option>)}
      </select>
      {!forms.length && <div className="text-[11px] text-[#B45309]" data-testid={`${testid}-empty`}>Aucun module actif n'ouvre de fiche aux clients : activez un module avec volet client dans « Modules ».</div>}
    </div>
  );
}

export const Lbl = ({ children }) => <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057]">{children}</Label>;

export function ImageField({ value, onChange, webApi, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await webApi.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image ajoutée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {value ? <img src={mediaUrl(value)} alt="" className="h-14 w-20 object-cover border border-[#DEE2E6] bg-[#F8F9FA]" /> : <div className="h-14 w-20 border border-dashed border-[#DEE2E6] bg-[#F8F9FA] text-[10px] text-[#868E96] flex items-center justify-center">Aucune</div>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={testid ? `${testid}-input` : undefined} />
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="h-8 text-xs" data-testid={testid ? `${testid}-upload` : undefined}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} {value ? "Remplacer" : "Téléverser"}
      </Button>
      {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} className="h-8 px-2 text-[#E60000]"><X className="h-3 w-3" /></Button>}
    </div>
  );
}

function ImagesField({ value = [], onChange, webApi }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const urls = [...value];
    for (const f of Array.from(files).slice(0, 24 - urls.length)) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await webApi.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(data.url); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    onChange(urls); setBusy(false); if (ref.current) ref.current.value = "";
  };
  return (
    <div>
      <div className="grid grid-cols-4 gap-2 mb-2">
        {value.map((u, i) => (
          <div key={u + i} className="relative group">
            <img src={mediaUrl(u)} alt="" className="h-20 w-full object-cover border border-[#DEE2E6]" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Retirer"><X className="h-3 w-3 text-[#E60000]" /></button>
          </div>
        ))}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      <Button type="button" size="sm" variant="outline" disabled={busy || value.length >= 24} onClick={() => ref.current?.click()} className="h-8 text-xs" data-testid="images-upload">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} Ajouter des photos ({value.length}/24)
      </Button>
    </div>
  );
}

function TagsField({ value = [], onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v && value.length < 8) { onChange([...value, v]); setDraft(""); } };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((t, i) => <span key={t + i} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-[#F1F3F5]">{t}<button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button></span>)}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="h-9 text-sm" />
        <Button type="button" size="sm" variant="outline" onClick={add} className="h-9"><Plus className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

function HoursField({ value = [], onChange }) {
  const set = (i, patch) => onChange(value.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <div className="space-y-1.5">
      {value.map((d, i) => (
        <div key={d.day} className="grid grid-cols-[90px_1fr_1fr_auto] items-center gap-2 text-xs">
          <span className="font-semibold">{d.day}</span>
          <Input type="time" value={d.open || ""} disabled={d.closed} onChange={(e) => set(i, { open: e.target.value })} className="h-8 text-xs" />
          <Input type="time" value={d.close || ""} disabled={d.closed} onChange={(e) => set(i, { close: e.target.value })} className="h-8 text-xs" />
          <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider"><Switch checked={!!d.closed} onCheckedChange={(v) => set(i, { closed: v })} /> Fermé</label>
        </div>
      ))}
    </div>
  );
}

function ListField({ field, value = [], onChange, webApi, Renderer }) {
  const [open, setOpen] = useState(null);
  const blank = Object.fromEntries(field.fields.map((f) => [f.key, f.type === "number" ? 0 : ""]));
  const update = (i, k, v) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => { const arr = [...value]; const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr); };
  return (
    <div className="space-y-2">
      {value.map((it, i) => (
        <div key={i} className="border border-[#DEE2E6] bg-white" data-testid={`list-item-${i}`}>
          <div className="flex items-center gap-2 px-3 py-2">
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="flex-1 text-left text-sm font-semibold truncate">{it[field.itemLabel] || `Élément ${i + 1}`}</button>
            <button type="button" onClick={() => move(i, -1)} className="p-1 text-[#868E96] hover:text-black"><ChevronUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} className="p-1 text-[#868E96] hover:text-black"><ChevronDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1 text-[#E60000]" data-testid={`list-item-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          {open === i && (
            <div className="px-3 pb-3 space-y-2 border-t border-[#F1F3F5] pt-2">
              {field.fields.map((sf) => <Renderer key={sf.key} field={sf} value={it[sf.key]} onChange={(v) => update(i, sf.key, v)} webApi={webApi} />)}
            </div>
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => { onChange([...value, blank]); setOpen(value.length); }} className="h-8 text-xs" data-testid="list-add"><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
    </div>
  );
}

export function FieldRenderer({ field, value, onChange, webApi }) {
  const tid = `field-${field.key}`;
  const body = (() => {
    switch (field.type) {
      case "textarea": return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} className="text-sm" data-testid={tid} placeholder={field.placeholder} />;
      case "number": return <Input type="number" step="0.01" min={field.min} max={field.max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="h-9 text-sm" data-testid={tid} />;
      case "date": return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" data-testid={tid} />;
      case "toggle": return <div className="flex items-center gap-2 h-9"><Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} /><span className="text-xs">{value ? "Oui" : "Non"}</span></div>;
      case "select": return (
        <select value={String(value ?? "")} onChange={(e) => { const o = field.options.find((x) => String(x.value) === e.target.value); onChange(o ? o.value : e.target.value); }} className="h-9 w-full px-2 text-sm border border-[#DEE2E6] bg-white" data-testid={tid}>
          {field.options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </select>);
      case "module_entity": return <ModuleEntityField value={value} onChange={onChange} testid={tid} />;
      case "image": return <ImageField value={value} onChange={onChange} webApi={webApi} testid={tid} />;
      case "images": return <ImagesField value={value || []} onChange={onChange} webApi={webApi} />;
      case "tags": return <TagsField value={value || []} onChange={onChange} placeholder={field.placeholder} />;
      case "hours": return <HoursField value={value || []} onChange={onChange} />;
      case "list": return <ListField field={field} value={value || []} onChange={onChange} webApi={webApi} Renderer={FieldRenderer} />;
      default: return <Input type={field.type === "url" ? "url" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm" data-testid={tid} placeholder={field.placeholder} />;
    }
  })();
  return <div className="space-y-1"><Lbl>{field.label}</Lbl>{body}</div>;
}
